import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { ShareDetailsModalService } from 'src/app/components/modal/share-details-modal/share-details-modal.service';
import { ProfileImageComponent } from 'src/app/components/profile-image/profile-image.component';
import { SharedUsersComponent } from 'src/app/components/shared-users/shared-users.component';
import * as myGlobals from 'src/app/globals';
import { ShareManagementResponseModal } from 'src/app/models/modal.model';
import { UserModel } from 'src/app/models/profile.model';
import { SharesService } from 'src/app/pages/shares/shares.service';
import { TrimStringPipe } from 'src/app/pipes/trim-string.pipe';
import { AuthStore } from 'src/app/services/auth.store';
import { CommonService } from 'src/app/services/common.service';
import { ModalService } from 'src/app/services/modal.service';
import { NotificationService } from 'src/app/services/notification.service';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as SgeCrypto from 'src/assets/lib/sge/sge-crypto';
@Component({
  selector: 'app-share-details-modal',
  standalone: true,
  imports: [ModalComponent, CommonModule, ReactiveFormsModule, FormsModule, ProfileImageComponent, SharedUsersComponent, TranslateModule],
  providers: [ModalService, ShareDetailsModalService, TrimStringPipe],
  templateUrl: './share-details-modal.component.html',
  styleUrls: ['./share-details-modal.component.scss'],
})
export class ShareDetailsModalComponent implements OnChanges, OnDestroy {
  subscription: Subscription = new Subscription();

  @Input() objectId?: string;
  @Input() modalOpen!: boolean;
  @Input() privateShareId?: string;
  @ViewChild('btnRefmodal') btnRefmodal!: ElementRef;

  //string
  modalTitle = this.commonService.getTranslatedMessage('SHARE_DETAILS');
  modalId = 'shareDetailsModal';
  shareNote_ = '';
  sharePermisionForm!: FormGroup;

  //data
  removedItems: any[] = [];
  updatedItems: any[] = [];
  sharedItems: ShareManagementResponseModal[] = [];
  userImageUrls: { [key: string]: string } = {};
  groupImageUrls: { [key: string]: string } = {};
  user!: UserModel;

  //boolean
  submitted = false;
  modalOut = false;
  openModal = false;
  isBulkShares?: boolean;
  hasRtukUrl = myGlobals.Config.hasRtukUrl;
  tempObjectId = '';
  myEmail = '';

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;
    const modalContainer = document.getElementById(this.modalId);
    const isModalClicked = targetElement?.contains(modalContainer);
    if (isModalClicked) {
      this.modalOut = true;
      this.submitted = false;
    }
  }

  constructor(
    private authStore: AuthStore,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private shareManagementModalService: ShareDetailsModalService,
    private sharesService: SharesService,
    public commonService: CommonService,
    public trimStringPipe: TrimStringPipe,
    private modalService: ModalService,
  ) {
    this.subscription.add(
      authStore.user$.subscribe((user) => {
        this.user = user;
      }),
    );
    this.createSharePermissionForm();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.tempObjectId = changes.privateShareId.currentValue;
    if (changes.privateShareId && this.privateShareId) {
      this.myEmail = this.user.email;
      this.getSharedList(changes.privateShareId.currentValue);
    }
  }

  getSharedList(id: string): void {
    this.shareManagementModalService.getSharesDetails(id).subscribe({
      next: (res: ShareManagementResponseModal[]) => {
        this.sharedItems = res;
      },
      error: () => {
        this.sharedItems = [];
      },
    });
  }

  createSharePermissionForm(): void {
    this.sharePermisionForm = this.fb.group({
      permission: new FormControl(),
      note: new FormControl(),
    });
  }

  toggleNote(item: ShareManagementResponseModal): void {
    item.showInput = !item.showInput;
    this.checkIfItemIncludes(item);
  }

  showLeaveShareSnackbar(item: ShareManagementResponseModal): void {
    const notification = {
      message: this.commonService.getTranslatedMessage('ARE_YOU_SURE_TO_LEAVE_SHARE'),
      actions: [
        { label: this.commonService.getTranslatedMessage('NO'), callback: () => {} },
        { label: this.commonService.getTranslatedMessage('YES'), callback: (): void => this.leaveShares(item) },
      ],
    };
    this.notificationService.defaultNotification(notification);
  }

  leaveShares(item: ShareManagementResponseModal): void {
    this.subscription = this.sharesService.leaveShares([item.privateShareId]).subscribe({
      next: () => {
        if (item.encryptedId) {
          SgeCrypto.leaveSharing(item.multipleObject ? item.privateShareId : item.encryptedId, this.user.email, item.multipleObject ? true : false).then(() => {
            const notification = {
              message: this.commonService.getTranslatedMessage('UNABLE_TO_LEAVE_ENCRYPTED_SHARE'),
            };
            this.notificationService.errorNotification(notification);
          });
        } else {
          const notification = {
            message: this.commonService.getTranslatedMessage('LEAVING_SHARE_SUCCESSFUL'),
          };
          this.notificationService.successNotification(notification);
        }
        this.getSharedList(item.privateShareId);
        this.btnRefmodal.nativeElement.click();
      },
      error: () => {
        const notification = {
          message: this.commonService.getTranslatedMessage('UNABLE_TO_LEAVE_SHARE'),
        };
        this.notificationService.errorNotification(notification);
      },
      complete: () => {
        this.commonService.rebindListener.next();
        this.commonService.handleSharesMultiChoice(undefined);
      },
    });
  }

  checkIfItemIncludes(item: ShareManagementResponseModal): void {
    const existingItem = this.updatedItems.find((i) => {
      if (item.shareTargetId === i.shareTargetId) {
        return true;
      }

      return false;
    });

    if (!existingItem) {
      this.updatedItems.push(item);
    }
  }

  closeModalButtonTrigger(): void {
    if (this.submitted) {
      this.updatedItems = [];
      this.removedItems = [];
      this.modalOpen = false;
    }
    this.removedItems = [];
    this.updatedItems = [];
    this.getSharedList(this.tempObjectId);
    this.modalService.closeModalManually(this.modalId);
  }

  getGroupName(group: any): string {
    return group.name;
  }
}
